import { ActionResult } from "../interfaces/ActionResult";
import { AlertLevel } from "../types/AlertTypes";

declare global {
  interface Window {
    rpgAction: any;
  }
}

export interface ActionDelegate {
  delegateKey: string;
  newActions: DefinedAction[];
}

export interface DefinedAction {
  actionName: string;
  paramNames: string[];
  allowOneLess: boolean;
  actionCallback: (...params: string[]) => ActionResult;
}

interface ActionCall {
  success: boolean;
  action: string;
  params: string[];
}

export class RpgActions {
  actions: string[] = [];
  delegateKeys: string[] = [];
  index: { [key: string]: number } = {};
  definedActions: { [key: string]: DefinedAction } = {};

  constructor() {
    window.rpgAction = this.rpgAction;
  }

  public reset = () => {
    this.actions = [];
    this.delegateKeys = [];
    this.definedActions = {};
  };

  public addActionsOrReset = (delegate: ActionDelegate | null) => {
    if (!delegate) {
      this.reset();
      return;
    }
    this.delegateKeys = [...this.delegateKeys, delegate.delegateKey];
    delegate.newActions.forEach((action) => {
      const newActionName = action.actionName.toLowerCase();
      if (this.actions.includes(newActionName)) {
        this.definedActions[this.index[newActionName]] = action;
        return;
      }
      this.actions.push(newActionName);
      this.rebuildIndex();
      this.definedActions[newActionName] = action;
    });
  };

  public rebuildIndex = () => {
    this.index = {};
    for (var i = 0; i < this.actions.length; i++) {
      this.index[this.actions[i]] = i;
    }
  };

  public screenAction = (action: string): ActionCall => {
    if (!action || action.trim().length === 0) {
      return { success: false, action: "", params: [] };
    }
    const actionSplit: string[] = (
      action.match(/['|"][^'|"]*['|"]|[^ ]+/g) || []
    ).map((item) => item.replace(/"|'/g, ""));
    if (actionSplit.length < 1) {
      return { success: false, action: "", params: [] };
    }
    const actionName = actionSplit[0].toLowerCase();
    if (!this.actions.includes(actionName)) {
      return {
        success: false,
        action: actionName,
        params: actionSplit.splice(1),
      };
    }
    return { success: true, action: actionName, params: actionSplit.splice(1) };
  };

  public rpgAction = (action: string): ActionResult => {
    const actionCall: ActionCall = this.screenAction(action);
    if (!actionCall.success) {
      const availableActions = this.actions
        .map((action) => {
          return this.definedActions[action].actionName;
        })
        .join(", ");
      alert(
        `No command called '${actionCall.action}'. Available actions are:\n${availableActions}`
      );
      return {
        success: false,
        message: `No command matching '${actionCall.action}'.`,
        level: AlertLevel._2_RULE,
      };
    }
    const definedAction: DefinedAction = this.definedActions[actionCall.action];
    let expectedParamsLength: number = definedAction.paramNames.length;
    if (definedAction.allowOneLess) {
      expectedParamsLength--;
    }
    if (expectedParamsLength <= actionCall.params.length) {
      const actionResult: ActionResult = definedAction.actionCallback(
        ...actionCall.params
      );
      return actionResult;
    } else {
      alert(
        `Action parameters don't match.\nExpected:\n<${definedAction.paramNames.join(
          ", "
        )}>\nActual:\n<${actionCall.params.join(", ")}>`
      );
      return {
        success: false,
        message: "Parameters aren't valid.",
        level: AlertLevel._2_RULE,
      };
    }
  };
}
